import { cloneDeep } from "lodash";
import { isWebsiteBuilderTabset, IWebsiteBuilderTabset } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTabset";
import { IWebsiteBuilderFooter } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderFooter";
import { IWebsiteBuilderHeader } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { IWebsiteBuilderTab } from "pages/secure/facility/websiteBuilder/editor/nodes/tab/websiteBuilderTab";
import { IWebsiteBuilderGeneralTraversalNode } from "../../actionHelpers/generalTraversal";
import {
  IWebsiteBuilderEditorHistoryActionBase,
  IWebsiteBuilderHistorySnapshot,
  WebsiteBuilderEditorActionType,
} from "../../websiteBuilderEditorAction";
import { IAddTabAction } from "./addTab";
import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";

export interface IDeleteTabActionPayload {
  tabsetId: string;
  tab: IWebsiteBuilderTab;
  index: number;
  prevDefaultTabId: string;
  nextDefaultTabId: string;
}

export interface IDeleteTabAction extends IWebsiteBuilderEditorHistoryActionBase {
  type: typeof WebsiteBuilderEditorActionType.DELETE_TAB;
  payload: IDeleteTabActionPayload;
}

export function getDeleteTabInverseAction(action: IDeleteTabAction): IAddTabAction {
  const {
    tabsetId,
    tab,
    index,
    prevDefaultTabId,
    nextDefaultTabId,
  } = action.payload;

  return {
    type: WebsiteBuilderEditorActionType.ADD_TAB,
    pageId: action.pageId,
    device: action.device,
    scrollNodeId: action.scrollNodeId,
    payload: {
      tabsetId,
      tab: cloneDeep(tab),
      index,
      prevDefaultTabId: nextDefaultTabId,
      nextDefaultTabId: prevDefaultTabId,
    },
  };
}

type TDeleteTabTraversalNode = IWebsiteBuilderTabset | IWebsiteBuilderGeneralTraversalNode;

function deleteTabTraversal(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  tabsetId: string,
  tabId: string,
  defaultTabId: string,
): IWebsiteBuilderHistorySnapshot {
  function traverse(node: TDeleteTabTraversalNode): TDeleteTabTraversalNode {
    if (isWebsiteBuilderTabset(node)) {
      if (node.id === tabsetId) {
        return {
          ...node,
          container: {
            ...node.container,
            settings: {
              ...node.container.settings,
              defaultTabId,
            },
          },
          children: node.children.filter(tab => tab.id !== tabId),
        };
      }

      return node;
    } else if (node.children) {
      return {
        ...node,
        children: node.children.map(traverse),
      };
    } else {
      return node;
    }
  }

  return {
    header: traverse(header) as IWebsiteBuilderHeader,
    root: traverse(root) as IWebsiteBuilderRoot,
    footer: traverse(footer) as IWebsiteBuilderFooter,
  };
}

export function applyDeleteTabChange(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  action: IDeleteTabAction,
): IWebsiteBuilderHistorySnapshot {
  const { tabsetId, tab, nextDefaultTabId } = action.payload;

  return deleteTabTraversal(header, root, footer, tabsetId, tab.id, nextDefaultTabId);
}
